import { SortAscIcon, SortDescIcon } from '@primer/octicons-react';
import { clsx } from 'clsx';
import React from 'react';
import Text from '../Text/Text.js';
import VisuallyHidden from '../_VisuallyHidden.js';
import { SortDirection } from './sorting.js';
import { useTableLayout } from './useTable.js';
import { SkeletonText } from '../experimental/Skeleton/SkeletonText.js';
import { ScrollableRegion } from '../ScrollableRegion/ScrollableRegion.js';
import { Button } from '../internal/components/ButtonReset.js';
import classes from './Table.module.css.js';
import { defaultSxProp } from '../utils/defaultSxProp.js';
import { BoxWithFallback } from '../internal/components/BoxWithFallback.js';

function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }

// ----------------------------------------------------------------------------
// Table
// ----------------------------------------------------------------------------

const Table = /*#__PURE__*/React.forwardRef(function Table({
  'aria-labelledby': labelledby,
  cellPadding = 'normal',
  className,
  gridTemplateColumns,
  ...rest
}, ref) {
  return (
    /*#__PURE__*/
    // TODO update type to be non-optional in next major release
    // @ts-expect-error this type should be required in the next major version
    React.createElement(ScrollableRegion, {
      "aria-labelledby": labelledby,
      className: clsx('TableOverflowWrapper', classes.TableOverflowWrapper)
    }, /*#__PURE__*/React.createElement("table", _extends({}, rest, {
      "aria-labelledby": labelledby,
      "data-cell-padding": cellPadding,
      className: clsx(className, 'Table', classes.Table),
      role: "table",
      ref: ref,
      style: {
        '--grid-template-columns': gridTemplateColumns
      }
    })))
  );
});

// ----------------------------------------------------------------------------
// TableHead
// ----------------------------------------------------------------------------

function TableHead({
  children
}) {
  return (
    /*#__PURE__*/
    // We need to explicitly pass this role because some ATs and browsers drop table semantics
    // when we use `display: contents` or `display: grid` in the table
    React.createElement("thead", {
      className: clsx('TableHead', classes.TableHead),
      role: "rowgroup"
    }, children)
  );
}
TableHead.displayName = "TableHead"; // ----------------------------------------------------------------------------
// TableBody
// ----------------------------------------------------------------------------
function TableBody({
  children
}) {
  return (
    /*#__PURE__*/
    // We need to explicitly pass this role because some ATs and browsers drop table semantics
    // when we use `display: contents` or `display: grid` in the table
    React.createElement("tbody", {
      className: clsx('TableBody', classes.TableBody),
      role: "rowgroup"
    }, children)
  );
}
TableBody.displayName = "TableBody"; // ----------------------------------------------------------------------------
// TableHeader
// ----------------------------------------------------------------------------
function TableHeader({
  align,
  children,
  ...rest
}) {
  return /*#__PURE__*/React.createElement("th", _extends({}, rest, {
    className: clsx('TableHeader', classes.TableHeader),
    role: "columnheader",
    scope: "col",
    "data-cell-align": align
  }), children);
}
TableHeader.displayName = "TableHeader";
function TableSortHeader({
  align,
  children,
  direction,
  onToggleSort,
  ...rest
}) {
  const ariaSort = direction === 'DESC' ? 'descending' : direction === 'ASC' ? 'ascending' : undefined;
  return /*#__PURE__*/React.createElement(TableHeader, _extends({}, rest, {
    "aria-sort": ariaSort,
    align: align
  }), /*#__PURE__*/React.createElement(Button, {
    type: "button",
    className: clsx('TableSortButton', classes.TableSortButton),
    onClick: () => {
      onToggleSort();
    }
  }, children, direction === SortDirection.NONE || direction === SortDirection.ASC ? /*#__PURE__*/React.createElement(SortAscIcon, {
    className: clsx('TableSortIcon', 'TableSortIcon--ascending', classes.TableSortIcon, classes['TableSortIcon--ascending'])
  }) : null, direction === SortDirection.DESC ? /*#__PURE__*/React.createElement(SortDescIcon, {
    className: clsx('TableSortIcon', 'TableSortIcon--descending', classes.TableSortIcon, classes['TableSortIcon--descending'])
  }) : null));
}
TableSortHeader.displayName = "TableSortHeader"; // ----------------------------------------------------------------------------
// TableRow
// ----------------------------------------------------------------------------
function TableRow({
  children,
  ...rest
}) {
  return /*#__PURE__*/React.createElement("tr", _extends({}, rest, {
    className: clsx('TableRow', classes.TableRow),
    role: "row"
  }), children);
}
TableRow.displayName = "TableRow"; // ----------------------------------------------------------------------------
// TableCell
// ----------------------------------------------------------------------------
function TableCell({
  align,
  className,
  children,
  scope,
  ...rest
}) {
  const BaseComponent = scope ? 'th' : 'td';
  const role = scope ? 'rowheader' : 'cell';
  return /*#__PURE__*/React.createElement(BaseComponent, _extends({}, rest, {
    className: clsx('TableCell', className, classes.TableCell),
    scope: scope,
    role: role,
    "data-cell-align": align
  }), children);
}
TableCell.displayName = "TableCell";
function TableCellPlaceholder({
  children
}) {
  return /*#__PURE__*/React.createElement(Text, {
    color: "fg.subtle"
  }, children);
}
TableCellPlaceholder.displayName = "TableCellPlaceholder"; // ----------------------------------------------------------------------------
// TableContainer
// ----------------------------------------------------------------------------
function TableContainer({
  children,
  sx: sxProp = defaultSxProp
}) {
  return /*#__PURE__*/React.createElement(BoxWithFallback, {
    className: clsx(classes.TableContainer),
    sx: sxProp
  }, children);
}
TableContainer.displayName = "TableContainer";
const TableTitle = /*#__PURE__*/React.forwardRef(function TableTitle({
  as: Component = 'h2',
  children,
  id
}, ref) {
  const BaseComponent = Component;
  return /*#__PURE__*/React.createElement(BaseComponent, {
    className: clsx('TableTitle', classes.TableTitle),
    id: id,
    ref: ref
  }, children);
});
function TableSubtitle({
  as: BaseComponent = 'div',
  children,
  id
}) {
  return /*#__PURE__*/React.createElement(BaseComponent, {
    className: clsx('TableSubtitle', classes.TableSubtitle),
    id: id
  }, children);
}
TableSubtitle.displayName = "TableSubtitle";
function TableDivider() {
  return /*#__PURE__*/React.createElement("div", {
    className: clsx('TableDivider', classes.TableDivider),
    role: "presentation"
  });
}
TableDivider.displayName = "TableDivider";
function TableActions({
  children
}) {
  return /*#__PURE__*/React.createElement("div", {
    className: clsx('TableActions', classes.TableActions)
  }, children);
}
TableActions.displayName = "TableActions"; // ----------------------------------------------------------------------------
// TableSkeleton
// ----------------------------------------------------------------------------
function TableSkeleton({
  cellPadding,
  columns,
  rows = 10,
  ...rest
}) {
  const {
    gridTemplateColumns
  } = useTableLayout(columns);
  return /*#__PURE__*/React.createElement(Table, _extends({}, rest, {
    cellPadding: cellPadding,
    gridTemplateColumns: gridTemplateColumns
  }), /*#__PURE__*/React.createElement(TableHead, null, /*#__PURE__*/React.createElement(TableRow, null, Array.isArray(columns) ? columns.map((column, i) => {
    return /*#__PURE__*/React.createElement(TableHeader, {
      key: i
    }, typeof column.header === 'string' ? column.header : column.header());
  }) : null)), /*#__PURE__*/React.createElement(TableBody, null, /*#__PURE__*/React.createElement(TableRow, null, Array.from({
    length: columns.length
  }).map((_, i) => {
    return /*#__PURE__*/React.createElement(TableCell, {
      key: i,
      className: clsx('TableCellSkeleton', classes.TableCellSkeleton)
    }, /*#__PURE__*/React.createElement(VisuallyHidden, null, "Loading"), /*#__PURE__*/React.createElement("div", {
      className: clsx('TableCellSkeletonItems', classes.TableCellSkeletonItems)
    }, Array.from({
      length: rows
    }).map((_, i) => {
      return /*#__PURE__*/React.createElement("div", {
        key: i,
        className: clsx('TableCellSkeletonItem', classes.TableCellSkeletonItem)
      }, /*#__PURE__*/React.createElement(SkeletonText, null));
    })));
  }))));
}
TableSkeleton.displayName = "TableSkeleton";

export { Table, TableActions, TableBody, TableCell, TableCellPlaceholder, TableContainer, TableDivider, TableHead, TableHeader, TableRow, TableSkeleton, TableSortHeader, TableSubtitle, TableTitle };
